import { DonationPicker, donationAmount } from './booking/DonationPicker';
import { WallOptIn } from './DonorWall';
import React, { useEffect, useState, useCallback } from 'react';
import { X, Loader2, Minus, Plus, Users } from 'lucide-react';
import api from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const sym = (c) => (c === 'inr' ? '₹' : '$');

const programDays = (p) => {
  if (p.start_datetime && p.end_datetime) {
    try {
      const s = new Date(p.start_datetime); const e = new Date(p.end_datetime);
      return Math.max(1, Math.ceil((e - s) / 86400000));
    } catch { return 1; }
  }
  return 1;
};

const Stepper = ({ label, value, setValue, min = 1, max = 99, hint }) => (
  <div className="flex items-center justify-between">
    <div>
      <div className="text-sm text-burgundy">{label}</div>
      {hint && <div className="text-xs text-mahogany/60">{hint}</div>}
    </div>
    <div className="flex items-center gap-2">
      <button onClick={() => setValue(Math.max(min, value - 1))} className="w-7 h-7 rounded-full bg-rose-soft/70 text-mahogany flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
      <span className="w-8 text-center text-burgundy font-medium">{value}</span>
      <button onClick={() => setValue(Math.min(max, value + 1))} className="w-7 h-7 rounded-full bg-rose-soft/70 text-mahogany flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
    </div>
  </div>
);

const BookingWidget = ({ program, onClose }) => {
  const isDonation = program.pricing_type === 'donation';
  const [donChoice, setDonChoice] = useState('suggested');
  const [donCustom, setDonCustom] = useState('');
  const [wall, setWall] = useState({ opt_in: false, style: 'first_initial' });
  const { toast } = useToast();
  const { user } = useAuth();
  const maxDays = programDays(program);
  const isChoice = program.pricing_type === 'flat_or_per_day';
  const isTickets = program.pricing_type === 'tickets';
  const isEnquiry = program.pricing_type === 'enquiry';
  const isHybrid = program.mode === 'hybrid';
  const [plan, setPlan] = useState('flat'); // used only when isChoice
  const [registrants, setRegistrants] = useState(1);
  const [numDays, setNumDays] = useState(program.pricing_type === 'per_day' ? maxDays : 1);
  const [addonQty, setAddonQty] = useState({});
  const [ticketQty, setTicketQty] = useState({});
  const [guestNames, setGuestNames] = useState([]);
  const [guestDietary, setGuestDietary] = useState([]);
  const [soloDietary, setSoloDietary] = useState('');
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [attendanceMode, setAttendanceMode] = useState('in_person');

  const s = sym(program.currency);
  const perDayActive = program.pricing_type === 'per_day' || (isChoice && plan === 'per_day');
  const allowDays = perDayActive && program.allow_day_selection;
  const allowReg = !isTickets && program.allow_multiple_registrants !== false;
  const ticketRemaining = (t) => (t.quantity_total ? Math.max(0, t.quantity_total - (t.quantity_sold || 0)) : null);
  const noTicketsChosen = isTickets && Object.values(ticketQty).every((q) => !q);

  const numPeople = isTickets
    ? Object.values(ticketQty).reduce((a, b) => a + (b || 0), 0)
    : (allowReg ? registrants : 1);

  // Keep the guest-name list sized to the number of people (prefill first with the booker).
  useEffect(() => {
    setGuestNames((prev) => {
      if (numPeople <= 1) return [];
      return Array.from({ length: numPeople }, (_, i) => prev[i] ?? (i === 0 ? (user?.full_name || '') : ''));
    });
    setGuestDietary((prev) => (numPeople <= 1 ? [] : Array.from({ length: numPeople }, (_, i) => prev[i] ?? '')));
  }, [numPeople, user]);

  const giftAmount = isDonation ? donationAmount(program, numPeople, donChoice, donCustom) : null;
  const giftBelowMin = isDonation && giftAmount < (program.donation_min || 0) * numPeople;
  const fetchQuote = useCallback(async () => {
    if (giftBelowMin) return;
    setLoading(true);
    try {
      const addons = Object.entries(addonQty)
        .filter(([, q]) => q > 0)
        .map(([addon_id, quantity]) => ({ addon_id, quantity }));
      const tickets = Object.entries(ticketQty)
        .filter(([, q]) => q > 0)
        .map(([ticket_type_id, quantity]) => ({ ticket_type_id, quantity }));
      const res = await api.post('/bookings/quote', {
        program_id: program.id,
        num_days: allowDays ? numDays : maxDays,
        registrants: allowReg ? registrants : 1,
        addons,
        tickets,
        ...(isChoice ? { plan } : {}),
        ...(isHybrid ? { attendance_mode: attendanceMode } : {}),
        ...(isDonation ? { donation_amount: giftAmount } : {}),
      });
      setQuote(res.data);
    } catch (e) {
      toast({ title: 'Could not calculate price', description: e?.response?.data?.detail });
    } finally { setLoading(false); }
  }, [program.id, numDays, registrants, addonQty, ticketQty, allowDays, allowReg, maxDays, isChoice, plan, isHybrid, attendanceMode, toast, isDonation, giftAmount, giftBelowMin]);

  useEffect(() => { if (isEnquiry) return; fetchQuote(); }, [fetchQuote, isEnquiry]);

  const [donation, setDonation] = useState(0);
  const [waiverAgreed, setWaiverAgreed] = useState(false);
  const [showWaiver, setShowWaiver] = useState(false);
  const [waiverText, setWaiverText] = useState('');

  const toggleWaiver = async () => {
    setShowWaiver((s) => !s);
    if (!waiverText) {
      try {
        const r = await api.get(`/bookings/waiver-text?program_id=${program.id}`);
        setWaiverText(r.data.text || '');
      } catch { setWaiverText('Liability waiver & release. By registering you accept the standard participation waiver.'); }
    }
  };

  const proceed = async () => {
    if (!waiverAgreed) {
      toast({ title: 'Please agree to the waiver', description: 'You must read and accept the liability waiver to register.' });
      return;
    }
    setPaying(true);
    try {
      const addons = Object.entries(addonQty).filter(([, q]) => q > 0).map(([addon_id, quantity]) => ({ addon_id, quantity }));
      const tickets = Object.entries(ticketQty).filter(([, q]) => q > 0).map(([ticket_type_id, quantity]) => ({ ticket_type_id, quantity }));
      const res = await api.post('/bookings/checkout', {
        program_id: program.id,
        num_days: allowDays ? numDays : maxDays,
        registrants: allowReg ? registrants : 1,
        addons,
        tickets,
        ...(isChoice ? { plan } : {}),
        ...(isHybrid ? { attendance_mode: attendanceMode } : {}),
        ...(isDonation ? { donation_amount: giftAmount, wall_opt_in: wall.opt_in, wall_name_style: wall.style } : {}),
        ...(numPeople > 1
          ? { guests: guestNames.map((n, i) => ({ name: n, dietary: guestDietary[i] || '' })) }
          : { dietary: soloDietary }),
        waiver_agreed: true,
        origin_url: window.location.origin,
      });
      if (res.data.free) {
        const amt = Number(donation) || 0;
        if (program.accept_donations && amt > 0) {
          try {
            const d = await api.post('/donations/checkout', {
              amount: amt,
              fund: `Donation — ${program.title}`,
              origin_url: window.location.origin,
            });
            window.location.href = d.data.checkout_url;
            return;
          } catch {
            toast({ title: 'Donation could not be started', description: 'Your free booking is confirmed — the donation step was skipped.' });
            /* fall through to free confirmation if donation checkout fails */
          }
        }
        window.location.href = `/payment/success?booking_id=${res.data.booking_id}&kind=free`;
      } else {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      toast({ title: 'Booking failed', description: e?.response?.data?.detail || 'Please try again.' });
      setPaying(false);
    }
  };

  if (isEnquiry) return <EnquiryForm program={program} onClose={onClose} />;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-cream rounded-3xl max-w-md w-full max-h-[92vh] overflow-y-auto p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif-display text-2xl text-burgundy">Book: {program.title}</h2>
          <button onClick={onClose} className="text-mahogany/70 hover:text-mahogany"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          {isHybrid && (
            <div data-testid="attendance-mode-block">
              <div className="text-xs uppercase tracking-widest text-mahogany/70 mb-2">How will you attend?</div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="attend-in-person"
                  onClick={() => setAttendanceMode('in_person')}
                  className={`p-3 rounded-2xl border text-left transition-colors ${attendanceMode === 'in_person' ? 'border-mahogany bg-rose-soft/50' : 'border-rose/40 bg-white/60 hover:bg-rose-soft/30'}`}>
                  <div className="text-sm font-medium text-burgundy">In person</div>
                  <div className="text-xs text-mahogany/60">{program.location || 'On site'}</div>
                </button>
                <button type="button" data-testid="attend-online"
                  onClick={() => setAttendanceMode('online')}
                  className={`p-3 rounded-2xl border text-left transition-colors ${attendanceMode === 'online' ? 'border-mahogany bg-rose-soft/50' : 'border-rose/40 bg-white/60 hover:bg-rose-soft/30'}`}>
                  <div className="text-sm font-medium text-burgundy">Online</div>
                  <div className="text-xs text-mahogany/60">Join from anywhere</div>
                </button>
              </div>
              {quote?.mode_remaining != null && (
                <p className={`text-[11px] mt-1.5 ${quote.mode_remaining <= 0 ? 'text-red-500 font-medium' : 'text-mahogany/70'}`} data-testid="mode-seats-left">
                  {quote.mode_remaining <= 0
                    ? `Sorry, the ${attendanceMode === 'online' ? 'online' : 'in-person'} option is sold out.`
                    : `${quote.mode_remaining} ${attendanceMode === 'online' ? 'online' : 'in-person'} seat${quote.mode_remaining > 1 ? 's' : ''} left.`}
                </p>
              )}
            </div>
          )}
          {isChoice && (
            <div>
              <div className="text-xs uppercase tracking-widest text-mahogany/70 mb-2">Choose your plan</div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="plan-flat"
                  onClick={() => setPlan('flat')}
                  className={`p-3 rounded-2xl border text-left transition-colors ${plan === 'flat' ? 'border-mahogany bg-rose-soft/50' : 'border-rose/40 bg-white/60 hover:bg-rose-soft/30'}`}>
                  <div className="text-sm font-medium text-burgundy">Full retreat</div>
                  <div className="text-lg font-serif-display text-burgundy">{s}{Number(program.price || 0).toFixed(2)}</div>
                </button>
                <button type="button" data-testid="plan-per-day"
                  onClick={() => setPlan('per_day')}
                  className={`p-3 rounded-2xl border text-left transition-colors ${plan === 'per_day' ? 'border-mahogany bg-rose-soft/50' : 'border-rose/40 bg-white/60 hover:bg-rose-soft/30'}`}>
                  <div className="text-sm font-medium text-burgundy">Pay per day</div>
                  <div className="text-lg font-serif-display text-burgundy">{s}{Number(program.per_day_price || 0).toFixed(2)}<span className="text-xs font-sans text-mahogany/60"> / day</span></div>
                </button>
              </div>
            </div>
          )}
          {isTickets && (
            <div>
              <div className="text-xs uppercase tracking-widest text-mahogany/70 mb-2">Select tickets</div>
              <div className="space-y-3">
                {(program.ticket_types || []).map((t) => {
                  const remaining = ticketRemaining(t);
                  const soldOut = remaining === 0;
                  return (
                    <div key={t.id} data-testid={`ticket-opt-${t.id}`}>
                      {soldOut ? (
                        <div className="flex items-center justify-between opacity-60">
                          <div className="text-sm text-burgundy">{t.name} — {s}{Number(t.price).toFixed(2)}</div>
                          <span className="text-xs text-red-500 font-medium">Sold out</span>
                        </div>
                      ) : (
                        <Stepper
                          label={`${t.name} — ${s}${Number(t.price).toFixed(2)}`}
                          hint={remaining !== null ? `${remaining} left` : undefined}
                          value={ticketQty[t.id] || 0}
                          setValue={(v) => setTicketQty((p) => ({ ...p, [t.id]: v }))}
                          min={0}
                          max={remaining !== null ? remaining : 99}
                        />
                      )}
                    </div>
                  );
                })}
                {(program.ticket_types || []).length === 0 && <div className="text-sm text-mahogany/60">No tickets available.</div>}
              </div>
            </div>
          )}
          {allowReg && <Stepper label="Registrants" value={registrants} setValue={setRegistrants} min={1} max={20} />}
          {allowDays && <Stepper label="Number of days" value={numDays} setValue={setNumDays} min={1} max={maxDays} hint={`Full program is ${maxDays} day(s)`} />}

          {numPeople > 1 && (
            <div className="pt-2 border-t border-rose/40" data-testid="guest-names-section">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-mahogany/70 mb-2">
                <Users className="w-3.5 h-3.5" /> Guest names
              </div>
              <div className="space-y-3">
                {guestNames.map((name, i) => (
                  <div key={i} className="space-y-1.5">
                    <input
                      value={name}
                      onChange={(e) => setGuestNames((prev) => prev.map((n, idx) => (idx === i ? e.target.value : n)))}
                      placeholder={i === 0 ? 'Guest 1 (you)' : `Guest ${i + 1} full name`}
                      data-testid={`guest-name-${i}`}
                      className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany"
                    />
                    <input
                      value={guestDietary[i] || ''}
                      onChange={(e) => setGuestDietary((prev) => prev.map((d, idx) => (idx === i ? e.target.value : d)))}
                      placeholder="Dietary / accessibility needs (optional)"
                      data-testid={`guest-dietary-${i}`}
                      className="w-full bg-white/50 rounded-xl border border-rose/40 px-3 py-1.5 text-xs text-burgundy focus:outline-none focus:border-mahogany"
                    />
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-mahogany/60 mt-1.5">We'll use these for room allocation, dietary planning and check-in.</p>
            </div>
          )}

          {numPeople === 1 && (
            <div className="pt-2 border-t border-rose/40">
              <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-mahogany/70 mb-1.5">
                <Users className="w-3.5 h-3.5" /> Dietary / accessibility needs
              </label>
              <input value={soloDietary} onChange={(e) => setSoloDietary(e.target.value)}
                placeholder="Optional — e.g. vegetarian, wheelchair access" data-testid="solo-dietary"
                className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
            </div>
          )}

          {isDonation && <DonationPicker program={program} people={numPeople} s={s} choice={donChoice} setChoice={setDonChoice} custom={donCustom} setCustom={setDonCustom} />}
          {isDonation && giftAmount > 0 && <WallOptIn wall={wall} setWall={setWall} />}

          {program.addons?.length > 0 && (
            <div className="pt-2 border-t border-rose/40">
              <div className="text-xs uppercase tracking-widest text-mahogany/70 mb-2">Add-ons</div>
              <div className="space-y-3">
                {program.addons.map((a) => (
                  <Stepper
                    key={a.id}
                    label={`${a.name} — ${s}${a.price}${a.unit_label ? ` (${a.unit_label})` : ''}`}
                    value={addonQty[a.id] || 0}
                    setValue={(v) => setAddonQty((p) => ({ ...p, [a.id]: v }))}
                    min={0}
                    max={a.max_quantity || 99}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Breakdown */}
        <div className="mt-5 p-4 rounded-2xl bg-white/70 border border-rose/40 text-sm space-y-1.5">
          {loading || !quote ? (
            <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</div>
          ) : (
            <>
              {isTickets
                ? quote.ticket_lines?.map((l) => <Row key={l.ticket_type_id} label={`${l.name} × ${l.quantity}`} value={`${s}${l.line_total.toFixed(2)}`} />)
                : isDonation ? (quote.base_subtotal > 0 && <Row label="Donation" value={`${s}${quote.base_subtotal.toFixed(2)}`} />)
                : quote.base_subtotal > 0 && <Row label={`Program fee${quote.pricing_type === 'per_day' ? ` (${quote.days} day${quote.days > 1 ? 's' : ''} × ${quote.registrants})` : quote.registrants > 1 ? ` (× ${quote.registrants})` : ''}`} value={`${s}${quote.base_subtotal.toFixed(2)}`} />}
              {quote.discount_amount > 0 && <Row label={`${quote.membership_tier} member discount (${quote.discount_percent}%)`} value={`− ${s}${quote.discount_amount.toFixed(2)}`} accent />}
              {quote.addon_lines?.map((l) => <Row key={l.addon_id} label={`${l.name} × ${l.quantity}`} value={`${s}${l.line_total.toFixed(2)}`} />)}
              <div className="pt-2 mt-1 border-t border-rose/40 flex justify-between font-serif-display text-xl text-burgundy">
                <span>Total</span><span>{isTickets && noTicketsChosen ? '—' : quote.is_free ? 'Free' : `${s}${quote.total.toFixed(2)}`}</span>
              </div>
              {!isDonation && quote.discount_percent === 0 && quote.base_subtotal > 0 && (
                <p className="text-[11px] text-mahogany/60 pt-1">Become a member to save {80}% on program fees.</p>
              )}
            </>
          )}
        </div>

        {program.accept_donations && quote?.is_free && (
          <div className="mt-4 p-4 rounded-2xl bg-rose-soft/40 border border-rose/50" data-testid="donation-block">
            <div className="text-sm text-burgundy font-medium">Support this offering 🌸</div>
            <p className="text-xs text-mahogany/70 mt-0.5 mb-3">This gathering is community-supported. A donation is completely optional and keeps it accessible for all.</p>
            <div className="grid grid-cols-4 gap-2">
              {[11, 21, 51, 108].map((v) => (
                <button key={v} type="button" onClick={() => setDonation(donation === v ? 0 : v)} data-testid={`donation-preset-${v}`}
                  className={`py-2 rounded-xl text-sm transition-colors border ${Number(donation) === v ? 'bg-burgundy text-cream border-burgundy' : 'bg-white/70 text-burgundy border-rose/50 hover:bg-white'}`}>${v}</button>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm text-mahogany/70">$</span>
              <input type="number" min="0" step="1" value={[11, 21, 51, 108].includes(Number(donation)) ? '' : (donation || '')}
                onChange={(e) => setDonation(Math.max(0, parseFloat(e.target.value) || 0))} placeholder="Custom amount" data-testid="donation-custom"
                className="flex-1 bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
              {Number(donation) > 0 && (
                <button type="button" onClick={() => setDonation(0)} data-testid="donation-clear" className="text-xs text-mahogany/60 hover:text-burgundy underline">Continue without donating</button>
              )}
            </div>
          </div>
        )}

        <label className="flex items-start gap-2.5 mt-5 cursor-pointer" data-testid="waiver-agree-label">
          <input type="checkbox" checked={waiverAgreed} onChange={(e) => setWaiverAgreed(e.target.checked)} data-testid="waiver-agree-checkbox" className="mt-0.5" />
          <span className="text-xs text-mahogany/80 leading-relaxed">
            I have read and agree to the{' '}
            <button type="button" onClick={toggleWaiver} className="text-rose-italic underline" data-testid="waiver-link">liability waiver &amp; release</button>, and I accept it on behalf of everyone in my party.
          </span>
        </label>
        {showWaiver && (
          <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-rose/40 bg-white/70 p-3 text-[11px] leading-relaxed text-mahogany/80 whitespace-pre-wrap" data-testid="waiver-text-panel">
            {waiverText || 'Loading…'}
          </div>
        )}

        <button onClick={proceed} disabled={paying || loading || !quote || !waiverAgreed || giftBelowMin || (isTickets && noTicketsChosen) || (isHybrid && quote?.mode_remaining != null && (quote.mode_remaining <= 0 || numPeople > quote.mode_remaining))} className="pill-btn justify-center w-full mt-3" data-testid="booking-proceed-btn">
          {paying && <Loader2 className="w-4 h-4 animate-spin" />}
          {isHybrid && quote?.mode_remaining != null && quote.mode_remaining <= 0 ? 'Sold out'
            : isTickets && noTicketsChosen ? 'Select a ticket'
            : quote?.is_free ? (Number(donation) > 0 ? `Confirm & Donate $${Number(donation)}` : 'Confirm Free Booking')
            : isDonation ? `Donate ${s}${Number(quote?.total || 0).toFixed(2)} & Register`
            : 'Proceed to Payment'}
        </button>
        {!quote?.is_free && <p className="text-[11px] text-center text-mahogany/60 mt-2" data-testid="wallets-note">Pay securely with Apple Pay, Google Pay or card</p>}
      </div>
    </div>
  );
};

const Row = ({ label, value, accent }) => (
  <div className="flex justify-between">
    <span className="text-burgundy/80">{label}</span>
    <span className={accent ? 'text-green-700 font-medium' : 'text-burgundy'}>{value}</span>
  </div>
);

const EnquiryForm = ({ program, onClose }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [f, setF] = useState({
    name: user?.full_name || '', email: user?.email || '', phone: user?.phone || '',
    num_people: 1, preferred_dates: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.name.trim() || !f.email.trim() || !f.phone.trim()) {
      toast({ title: 'Please add your name, email and phone.' });
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/programs/${program.id}/enquiry`, {
        name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim(),
        num_people: Number(f.num_people) || 1,
        preferred_dates: f.preferred_dates.trim(), message: f.message.trim(),
      });
      setDone(true);
    } catch (e) {
      toast({ title: 'Could not send request', description: e?.response?.data?.detail, variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const field = 'w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany';

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="enquiry-modal">
      <div className="bg-cream rounded-3xl max-w-md w-full max-h-[92vh] overflow-y-auto p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-serif-display text-2xl text-burgundy">Request to Register</h2>
          <button onClick={onClose} className="text-mahogany/70 hover:text-mahogany"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-mahogany/70 mb-5">{program.title} — registration for this program is handled by phone. Send your details and our team will call you to complete it.</p>

        {done ? (
          <div className="text-center py-8" data-testid="enquiry-success">
            <div className="w-14 h-14 rounded-full bg-green-100 text-green-700 flex items-center justify-center mx-auto mb-4"><Users className="w-6 h-6" /></div>
            <h3 className="font-serif-display text-xl text-burgundy mb-2">Request sent</h3>
            <p className="text-sm text-mahogany/75">Thank you, {f.name.split(' ')[0] || 'friend'}. We've received your request and will contact you shortly. A confirmation is on its way to your email.</p>
            <button onClick={onClose} className="pill-btn mt-6" data-testid="enquiry-done-btn">Close</button>
          </div>
        ) : (
          <div className="space-y-3">
            <div><label className="text-xs uppercase tracking-widest text-mahogany/70">Full name *</label>
              <input className={field} value={f.name} onChange={(e) => set('name', e.target.value)} data-testid="enquiry-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs uppercase tracking-widest text-mahogany/70">Email *</label>
                <input type="email" className={field} value={f.email} onChange={(e) => set('email', e.target.value)} data-testid="enquiry-email-input" /></div>
              <div><label className="text-xs uppercase tracking-widest text-mahogany/70">Phone *</label>
                <input className={field} value={f.phone} onChange={(e) => set('phone', e.target.value)} data-testid="enquiry-phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs uppercase tracking-widest text-mahogany/70">Number of people</label>
                <input type="number" min="1" className={field} value={f.num_people} onChange={(e) => set('num_people', e.target.value)} data-testid="enquiry-people" /></div>
              <div><label className="text-xs uppercase tracking-widest text-mahogany/70">Preferred dates</label>
                <input className={field} value={f.preferred_dates} onChange={(e) => set('preferred_dates', e.target.value)} placeholder="e.g. first week of July" data-testid="enquiry-dates" /></div>
            </div>
            <div><label className="text-xs uppercase tracking-widest text-mahogany/70">Message (optional)</label>
              <textarea rows={3} className={field} value={f.message} onChange={(e) => set('message', e.target.value)} data-testid="enquiry-message" /></div>
            <button onClick={submit} disabled={submitting} className="pill-btn justify-center w-full mt-2 disabled:opacity-60" data-testid="enquiry-submit-btn">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Request to Register
            </button>
          </div>
        )}
      </div>
    </div>
  );
};


export default BookingWidget;
